import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqCategories = [
  { id: 'all', label: 'All Questions' },
  { id: 'pricing', label: 'Pricing & Timeline' },
  { id: 'process', label: 'Process & Communication' },
  { id: 'quality', label: 'Quality & Security' },
  { id: 'ownership', label: 'Ownership & Support' },
];

const faqItems: FAQItem[] = [
  {
    category: 'pricing',
    question: 'How do you price projects? Are there hidden fees?',
    answer:
      'We operate on transparent fixed-price or time-and-materials contracts with detailed Statements of Work. Every estimate includes development hours, design, QA, project management, and infrastructure setup. There are no hidden fees — any scope change is discussed and approved by you before work begins. For enterprise engagements, we offer retainer-based pricing with predictable monthly billing.',
  },
  {
    category: 'pricing',
    question: 'What is a typical timeline for an MVP?',
    answer:
      'Most MVPs ship in 8 to 14 weeks depending on complexity. A simple web app with authentication, core features, and a dashboard typically takes 8–10 weeks. A more complex platform with real-time features, AI integrations, or mobile apps ranges from 12–16 weeks. We provide a detailed sprint-by-sprint roadmap in the discovery phase so you know exactly what ships when.',
  },
  {
    category: 'pricing',
    question: 'Can we start small and scale the engagement?',
    answer:
      'Absolutely. Many clients begin with a 2-week discovery sprint or a single feature module to validate the fit. Once you are confident in our process and output, we scale the team up with dedicated engineers, designers, and QA. This phased approach eliminates risk and lets you evaluate ROI before committing to a full build.',
  },
  {
    category: 'process',
    question: 'How will we communicate during the project?',
    answer:
      'You get a dedicated Project Manager and direct Slack or Teams access to your engineering team. We run daily standups, bi-weekly sprint reviews, and monthly stakeholder demos. All code is hosted on your GitHub or GitLab account, and sprint boards are managed in Jira or Linear — fully transparent. You are never more than a message away from an update.',
  },
  {
    category: 'process',
    question: 'What if requirements change mid-project?',
    answer:
      'Change is expected, not feared. We work in agile two-week sprints, so priorities can be re-evaluated at every sprint boundary. For larger scope changes, we provide an impact analysis — timeline and cost delta — before you approve anything. This keeps you in control while maintaining delivery momentum.',
  },
  {
    category: 'process',
    question: 'Will your team work in our time zone?',
    answer:
      'Yes. We align working hours to provide at least 4–6 hours of overlap with your local team. For US clients, this means real-time collaboration during EST or PST business hours. For European clients, we offer near-full overlap. We also schedule demos, reviews, and planning sessions at times that work for your stakeholders.',
  },
  {
    category: 'quality',
    question: 'How do you ensure code quality and reliability?',
    answer:
      'Every pull request requires peer review and automated CI/CD checks before merge. We maintain 80%+ test coverage on business-critical paths, run security scans on every build, and enforce linting and type-checking across the codebase. Our Tech Leads review architecture decisions weekly, and we conduct automated performance regression testing before each release.',
  },
  {
    category: 'quality',
    question: 'Is our data and IP protected?',
    answer:
      'Yes. All engagements begin with signed NDAs and IP assignment agreements. Your source code lives in repositories you own and control. We implement role-based access controls, encrypted secrets management, and SOC 2-aligned security practices. For regulated industries, we support HIPAA, GDPR, and PCI-DSS compliance requirements.',
  },
  {
    category: 'quality',
    question: 'What testing and QA processes do you follow?',
    answer:
      'We integrate QA from day one — not as a final gate. Each sprint includes unit tests, integration tests, and manual exploratory testing. For releases, we run end-to-end suites in Cypress or Playwright, load testing with k6, and cross-browser/device validation. Security testing includes dependency vulnerability scanning and static code analysis.',
  },
  {
    category: 'ownership',
    question: 'Who owns the source code and intellectual property?',
    answer:
      'You do. From the first commit, all code is pushed to repositories under your organization. Our contract explicitly assigns all IP — source code, designs, documentation, and configurations — to you upon payment. We never reuse your proprietary logic for other clients, and all contractors sign IP assignment clauses before touching your codebase.',
  },
  {
    category: 'ownership',
    question: 'What happens after launch? Do you provide support?',
    answer:
      'We offer a 30 to 90-day warranty period post-launch covering bug fixes at no additional cost. Beyond that, we provide flexible maintenance retainers — from ad-hoc support to dedicated monthly engineering hours. Most clients transition into an ongoing partnership for feature enhancements, performance tuning, and platform scaling.',
  },
  {
    category: 'ownership',
    question: 'Can we take the project in-house after delivery?',
    answer:
      'Absolutely. We design every project for handoff success. Delivery includes full technical documentation, architecture diagrams, API specs, deployment runbooks, and onboarding sessions for your internal team. If you want to hire your own engineers later, they will have everything they need to take over seamlessly.',
  },
];

export default function SoftwareDevFAQ() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // FAQPage Schema.org structured data for rich results
  useEffect(() => {
    const faqSchemaData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };

    const scriptId = 'softdev-faq-schema-ld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqSchemaData);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  const filteredItems =
    activeCategory === 'all'
      ? faqItems
      : faqItems.filter((item) => item.category === activeCategory);

  const toggleItem = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      id="faq"
      className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden"
      ref={ref}
    >
      <div className="absolute top-20 right-0 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-10 left-0 w-72 h-72 bg-teal-100 rounded-full mix-blend-multiply filter blur-3xl opacity-15"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-question-answer-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Pre-Sale FAQ</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Common Questions
            <span className="text-primary-600"> Answered</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Everything you need to know before starting a software project with Thoughtic. Transparency is how we build trust.
          </motion.p>

          {/* Stat Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto"
          >
            {[
              { icon: 'ri-book-open-line', value: '12', label: 'FAQ Topics', accent: 'bg-primary-50 border-primary-100 text-primary-700' },
              { icon: 'ri-time-line', value: '24h', label: 'Response Time', accent: 'bg-teal-50 border-teal-100 text-teal-700' },
              { icon: 'ri-shield-check-line', value: '100%', label: 'IP Ownership', accent: 'bg-amber-50 border-amber-100 text-amber-700' },
              { icon: 'ri-customer-service-2-line', value: '30d', label: 'Free Warranty', accent: 'bg-sky-50 border-sky-100 text-sky-700' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.35 + idx * 0.06, ...springTransition }}
                className={`flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${stat.accent}`}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
                  <i className={`${stat.icon} text-base md:text-lg`}></i>
                </div>
                <div className="text-left">
                  <div className="text-sm md:text-base font-bold leading-tight">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-medium opacity-80">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12"
        >
          {faqCategories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setExpandedIndex(null);
                }}
                className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm md:text-base font-medium transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-primary-500 text-white border-primary-500 shadow-lg shadow-primary-500/20'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => {
              const isExpanded = expandedIndex === index;

              return (
                <motion.div
                  key={`${activeCategory}-${index}`}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    ...springTransition,
                    duration: 0.4,
                    delay: index * 0.04,
                  }}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${
                    isExpanded
                      ? 'border-primary-300 shadow-lg shadow-primary-500/5'
                      : 'border-gray-100 hover:border-primary-200 hover:shadow-md'
                  }`}
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full flex items-start justify-between p-5 md:p-6 text-left cursor-pointer gap-4"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div
                        className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-colors duration-300 ${
                          isExpanded
                            ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20'
                            : 'bg-gray-50 text-primary-600 border border-primary-200'
                        }`}
                      >
                        <i
                          className={`${isExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-base md:text-lg`}
                        ></i>
                      </div>
                      <span className="text-sm md:text-base font-semibold text-navy-900 leading-snug pt-1.5">
                        {item.question}
                      </span>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                          <div className="pl-11 md:pl-14 border-l-2 border-primary-200">
                            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Still Have Questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-10 md:mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-navy-900 rounded-2xl md:rounded-3xl px-6 py-5 md:px-10 md:py-6 shadow-xl shadow-navy-900/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0 shadow-lg shadow-primary-500/20">
                <i className="ri-mail-send-line text-white text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-white font-semibold text-sm md:text-base">
                  Still have questions?
                </p>
                <p className="text-gray-400 text-xs md:text-sm">
                  Our team is happy to discuss your specific project needs.
                </p>
              </div>
            </div>
            <a
              href="#cta"
              className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Get a Free Roadmap
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}